"use client";

import type { ReactNode } from "react";
import { type Option, SelectField } from "@/components/tool-kit/primitives";
import { HINT, INPUT_MONO, LABEL, SELECT } from "@/components/tool-kit/styles";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  formatSig,
  type NumberFormatter,
} from "@/lib/tool-kit/physics-fields/format";
import { typedValue } from "@/lib/tool-kit/physics-fields/input";
import { fromBase, type UnitDef } from "@/lib/tool-kit/physics-fields/units";

/*
 * Pieces shared by the physics field-and-force calculators: a number with a unit menu,
 * the working box, the significant-figures menu and the figure frame. Every visible word
 * arrives through props; unit symbols are data, not prose.
 */

/** The working box: arithmetic in monospace, on a quiet background. */
export const WORKING_BOX =
  "space-y-1 rounded-lg border border-border bg-muted/30 p-4 font-mono text-muted-foreground text-sm leading-relaxed";

export function unitOptions(units: readonly UnitDef[]): Option[] {
  return units.map((unit) => ({ value: unit.id, label: unit.symbol }));
}

/** A number box with its unit menu beside it. */
export function QuantityField({
  id,
  label,
  value,
  onChange,
  units,
  unit,
  onUnit,
  unitLabel,
  hint,
  solved = false,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  units: readonly UnitDef[];
  unit: string;
  onUnit: (unit: string) => void;
  /** Accessible name of the unit menu, e.g. "Unit for mass". */
  unitLabel: string;
  hint?: ReactNode;
  /** The unknown: shows the computed value, read-only, in the chosen unit. */
  solved?: boolean;
}) {
  return (
    <div className="space-y-2">
      <label className={LABEL} htmlFor={id}>
        {label}
      </label>
      <div className="grid grid-cols-[minmax(0,1fr)_7rem] gap-2">
        <input
          className={
            solved
              ? `${INPUT_MONO} border-primary/40 bg-primary/5 font-semibold`
              : INPUT_MONO
          }
          id={id}
          inputMode="decimal"
          onChange={(event) => onChange(event.target.value)}
          readOnly={solved}
          value={value}
        />
        <select
          aria-label={unitLabel}
          className={SELECT}
          id={`${id}-unit`}
          onChange={(event) => onUnit(event.target.value)}
          value={unit}
        >
          {units.map((option) => (
            <option key={option.id} value={option.id}>
              {option.symbol}
            </option>
          ))}
        </select>
      </div>
      {hint ? <p className={HINT}>{hint}</p> : null}
    </div>
  );
}

/** Lines of working in the shared box, with a heading. */
export function WorkingLines({
  heading,
  lines,
}: {
  heading: string;
  lines: readonly string[];
}) {
  return (
    <div className={WORKING_BOX}>
      <p className="font-medium font-sans text-foreground">{heading}</p>
      {lines.map((line, index) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: lines are positional text
        <p key={index}>{line}</p>
      ))}
    </div>
  );
}

const SIG_CHOICES = ["3", "4", "5", "6"] as const;
export type SigChoice = (typeof SIG_CHOICES)[number];

/** "Significant figures" menu: `message` is a plural message with a {count} slot. */
export function SigFigsField({
  id,
  label,
  message,
  locale,
  format,
  value,
  onChange,
}: {
  id: string;
  label: string;
  message: string;
  locale: string;
  format: (n: number) => string;
  value: SigChoice;
  onChange: (value: SigChoice) => void;
}) {
  const options = SIG_CHOICES.map((choice) => ({
    value: choice,
    label: tPlural(message, locale, Number(choice), {
      count: format(Number(choice)),
    }).trim(),
  }));
  return (
    <SelectField
      id={id}
      label={label}
      onChange={onChange}
      options={options}
      value={value}
    />
  );
}

/** A figure with an accessible name and a quiet frame. */
export function DiagramFrame({
  caption,
  children,
}: {
  caption: string;
  children: ReactNode;
}) {
  return (
    <figure className="space-y-2">
      <div className="mx-auto max-w-xl overflow-hidden rounded-lg border border-border">
        {children}
      </div>
      <figcaption className={`${HINT} text-center`}>{caption}</figcaption>
    </figure>
  );
}

/* ── Solve-for grids ──────────────────────────────────────────────────────── */

export interface SolveFieldSpec<K extends string> {
  key: K;
  label: string;
  units: readonly UnitDef[];
  /** This box is the unknown: it shows the computed value, read-only. */
  solved: boolean;
  hint?: string;
}

/**
 * The boxes of a "solve for" calculator. Known boxes echo what was typed; the unknown
 * shows the computed value, in the unit chosen in its own menu, to `sig` figures.
 */
export function SolveGrid<K extends string>({
  idPrefix,
  specs,
  text,
  unit,
  solvedSI,
  onText,
  onUnit,
  fmt,
  sig,
  unitTemplate,
}: {
  idPrefix: string;
  specs: readonly SolveFieldSpec<K>[];
  text: Record<K, string>;
  unit: Record<K, string>;
  /** The computed value of each unknown, in SI — null when there is no answer yet. */
  solvedSI: Partial<Record<K, number | null>>;
  onText: (key: K, value: string) => void;
  onUnit: (key: K, unit: string) => void;
  fmt: NumberFormatter;
  sig: number;
  /** "Unit for {name}" */
  unitTemplate: string;
}) {
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      {specs.map((spec) => {
        const si = solvedSI[spec.key] ?? null;
        let shown = text[spec.key];
        if (spec.solved) {
          shown =
            si === null
              ? ""
              : formatSig(fmt, fromBase(spec.units, unit[spec.key], si), sig);
        }
        return (
          <QuantityField
            hint={spec.hint}
            id={`${idPrefix}-${spec.key}`}
            key={spec.key}
            label={spec.label}
            onChange={(value) => onText(spec.key, value)}
            onUnit={(value) => onUnit(spec.key, value)}
            solved={spec.solved}
            unit={unit[spec.key]}
            unitLabel={t(unitTemplate, { name: spec.label })}
            units={spec.units}
            value={shown}
          />
        );
      })}
    </div>
  );
}

/**
 * When the unknown changes, the value that was just computed becomes an ordinary input,
 * so the reader can turn "find F" into "find x" without retyping.
 */
export function carrySolved<K extends string>(
  specs: readonly SolveFieldSpec<K>[],
  text: Record<K, string>,
  unit: Record<K, string>,
  solvedSI: Partial<Record<K, number | null>>,
  sig: number
): Record<K, string> {
  const previous = specs.find((spec) => spec.solved);
  const si = previous ? (solvedSI[previous.key] ?? null) : null;
  if (!previous || si === null) {
    return text;
  }
  return {
    ...text,
    [previous.key]: typedValue(
      fromBase(previous.units, unit[previous.key], si),
      sig
    ),
  };
}
